export const RECIPIENT_CSV_LABELS = `世帯識別符号
計算開始（年）
計算開始（月）
計算開始（日）
月末まで
計算終了（年）
計算終了（月）
計算終了（日）
世帯の保護開始
世帯の保護廃止
算定方法
算定方法オプション
世帯員識別符号
居宅等区分
級地
冬季加算区分
死亡フラグ
障害者加算_種別
母子加算_対象人数
未成年者控除
年齢
生年月日（年）
生年月日（月）
生年月日（日）
日用品費・基本生活費率
妊産婦加算_種別
在宅患者加算_種別
放射線障害者加算_種別
介護施設入所者加算_種別
特別基準
基準生活費（居宅）
冬季加算（居宅）
期末一時扶助（居宅）
基準生活費（単独）
冬季加算（単独）
期末一時扶助（単独）
障害者加算（居宅）
母子加算（居宅）
妊産婦加算
在宅患者加算
放射線障害者加算
基準生活費（救護施設等）
冬季加算（救護施設等）
期末一時扶助（救護施設等）
入院患者日用品費
介護施設入所者基本生活費
冬季加算（入院入所）
期末一時扶助（入院入所）
障害者加算（施設）
母子加算（施設）
介護施設入所者加算
決定通知書宛名
決定通知書郵便番号（ハイフンなし）
決定通知書住所
決定通知書処分庁
決定通知書教示文非表示
決定通知書審査請求先
決定通知書被告
決定通知書被告代表
実施機関コード１
実施機関コード２
担当CWコード
担当（地区）コード
ケース番号
員番号
事務処理用１
事務処理用２
事務処理用３
事務処理用４
事務処理用５
事務処理用６
事務処理用７
事務処理用８
事務処理用９`
  // 旧 FY2601〜FY2607（決定通知書追加情報・受給者口座情報）は 2026-05 改訂で CSV から撤去
  .trim()
  .split(/\n/);

export const RECIPIENT_FIELD_DEFS = RECIPIENT_CSV_LABELS.map((label: string, i: number) => ({ key: 'f' + (i + 1), label }));

/** f52〜f81 の世帯レベルフィールド定義 */
export const HOUSEHOLD_FIELD_DEFS = RECIPIENT_FIELD_DEFS.slice(51); // f52 onwards

/** f1〜f51 の世帯員レベルフィールド定義 */
export const MEMBER_FIELD_DEFS = RECIPIENT_FIELD_DEFS.slice(0, 51); // f1〜f51

export const RECIPIENT_BOOLEAN_FIELD_KEYS = new Set(['f17', 'f56']);

// ─────────────────────────────────────────────────────────────────────────
// 世帯共通項目（明細間一致チェック対象）
// 後端 recipients テーブルは「明細1行目（世帯主）」の値のみを採用する。
// 2026-06 改訂で Z004〜Z008（処分庁/教示文非表示/審査請求先/被告/被告代表 = f55〜f59）は
// 世帯一致チェックの対象外になった。特に f56（決定通知書教示文非表示）は受給者画面の
// 世帯単位チェックボックスへ再設計され、明細間で値が異なっても取込／保存を阻害しない。
// 決定通知書宛名/郵便番号/住所（f52/f53/f54 = Z001〜Z003）も header(recipients) 独立列
// （decision_notice_addressee/postal_code/address）になったため、画面側の一致チェックは行わない。
// → 上記(Z001〜Z008)は対象外。ただし 世帯識別符号（f1）は世帯の識別キーであり、
//    1世帯（= 1受給者）の明細全行で必ず一致していなければならないため、一致チェック対象とする。
//    （空欄の行は「未入力」として比較対象外。非空で1行目と異なる値のみ不一致として検出。）
//    バックエンド CSV 取込側は HouseholdConsistencyValidator が Z001〜Z003 のみ検査する。
// ─────────────────────────────────────────────────────────────────────────
export const HOUSEHOLD_CONSISTENCY_FIELD_KEYS: readonly string[] = ['f1'] as const;

/**
 * 比較用に正規化する。
 * - 前後空白の除去
 * - boolean 項目 (f17/f56) は "true"⇔"1", "false"⇔"0" を同一視
 */
export function normalizeHouseholdValue(fieldKey: string, value: unknown): string {
  const s = (value ?? '').toString().trim();
  if (!RECIPIENT_BOOLEAN_FIELD_KEYS.has(fieldKey)) return s;
  const lower = s.toLowerCase();
  if (lower === 'true' || lower === '1') return '1';
  if (lower === 'false' || lower === '0') return '0';
  return lower;
}

export interface HouseholdConsistencyViolation {
  rowIdx: number;       // 0-based 明細行インデックス。常に >= 1（1行目は基準）
  fieldKey: string;     // 例: 'f52'
  label: string;        // 表示ラベル（RECIPIENT_CSV_LABELS から取得）
  expected: string;     // 1行目の正規化済み値（"" = 空欄）
  actual: string;       // 当該行の正規化済み値
}

/**
 * 同一世帯内の明細行配列について、世帯共通 8 項目の不一致を検出する。
 * 2行目以降が空欄の場合は未指定として扱い、1行目との比較対象外にする。
 *
 * @param detailRows 明細行配列（各要素は {fieldKey -> value} のマップ）
 * @param row0Overrides 1行目の値を上書きするオプション。明細1行目より優先したい header 値が
 *                     ある場合にその上書き値をここで与える（現状 f55〜f59 のみが検査対象）。
 */
export function findHouseholdConsistencyViolations(
  detailRows: Record<string, unknown>[],
  row0Overrides?: Partial<Record<string, string>>,
): HouseholdConsistencyViolation[] {
  if (detailRows.length < 2) return [];

  const row0Raw = detailRows[0] || {};
  const row0Effective: Record<string, string> = {};
  for (const key of HOUSEHOLD_CONSISTENCY_FIELD_KEYS) {
    const override = row0Overrides?.[key];
    row0Effective[key] = normalizeHouseholdValue(
      key,
      override !== undefined ? override : row0Raw[key],
    );
  }

  const out: HouseholdConsistencyViolation[] = [];
  for (let rowIdx = 1; rowIdx < detailRows.length; rowIdx++) {
    const row = detailRows[rowIdx] || {};
    for (const key of HOUSEHOLD_CONSISTENCY_FIELD_KEYS) {
      const actual = normalizeHouseholdValue(key, row[key]);
      const expected = row0Effective[key];
      if (actual === '') continue;
      if (actual === expected) continue;
      const colIdx = parseInt(key.replace('f', ''), 10) - 1;
      const label = RECIPIENT_CSV_LABELS[colIdx] || key;
      out.push({ rowIdx, fieldKey: key, label, expected, actual });
    }
  }
  return out;
}

/** UI 表示用のメッセージ文言ヘルパー */
export function householdConsistencyMessage(v: HouseholdConsistencyViolation): string {
  const headDisplay = v.expected === '' ? '（空欄）' : `「${v.expected}」`;
  return `1行目と同じ値を入力してください（1行目: ${headDisplay}）`;
}

/**
 * 死亡として扱う世帯員の識別符号(f13)集合を返す。
 * 死亡判定は「死亡フラグ f17 が true/1」または「死亡情報入力(f13のカンマ区切り)に含まれる」の
 * いずれか（業務確認 2026-06: 両シグナルの和集合）。
 *
 * @param detailRows 明細行（各行は f13 / f17 を含む {fieldKey -> value} マップ。値は文字列/真偽どちらでも可）
 * @param deathInfo  死亡情報入力（recipient.deathInfo。f13 のカンマ区切り）
 */
export function computeDeceasedMemberCodes(
  detailRows: Array<Record<string, unknown>>,
  deathInfo: string | null | undefined,
): Set<string> {
  const deceased = new Set<string>();
  for (const token of (deathInfo ?? '').split(',').map((s) => s.trim()).filter(Boolean)) {
    deceased.add(token);
  }
  for (const row of detailRows) {
    const code = (row?.f13 ?? '').toString().trim();
    if (!code) continue;
    const flag = (row?.f17 ?? '').toString().trim().toLowerCase();
    if (flag === '1' || flag === 'true') {
      deceased.add(code);
    }
  }
  return deceased;
}

/** 明細1行（f13/f17 を持つ）が死亡扱いかを返す。 */
export function isDetailRowDeceased(
  row: Record<string, unknown> | undefined,
  deceasedCodes: Set<string>,
): boolean {
  if (!row) return false;
  const flag = (row.f17 ?? '').toString().trim().toLowerCase();
  if (flag === '1' || flag === 'true') return true;
  const code = (row.f13 ?? '').toString().trim();
  return code !== '' && deceasedCodes.has(code);
}

/**
 * 表示すべき世帯主氏名(世帯員識別符号 f13)を返す。
 * 業務ルール: 死亡者は世帯主になれない。明細1行目(本来の世帯主)が死亡扱いの場合は、
 * 死亡していない世帯員を明細の上から順に探し、最初に見つかった生存世帯員の f13 を世帯主とする。
 *
 * - 明細1行目が生存 → 従来通り header の世帯主氏名(storedHeadName)をそのまま返す。
 * - 明細1行目が死亡 → 2行目以降で最初に死亡していない世帯員の f13 を返す。
 * - 明細なし／全員死亡 → 兜底として storedHeadName を返す。
 *
 * @param detailRows  明細行（各行は f13 / f17 を含む {fieldKey -> value} マップ）
 * @param deceasedCodes computeDeceasedMemberCodes() の戻り値（死亡扱いの f13 集合）
 * @param storedHeadName header(recipients.household_head_name) の世帯主氏名（明細1行目の f13 と同値）
 */
export function effectiveHouseholdHeadName(
  detailRows: Array<Record<string, unknown>>,
  deceasedCodes: Set<string>,
  storedHeadName?: string | null,
): string {
  const stored = (storedHeadName ?? '').toString().trim();
  if (detailRows.length === 0) return stored;
  if (!isDetailRowDeceased(detailRows[0], deceasedCodes)) return stored;
  for (let i = 1; i < detailRows.length; i++) {
    if (!isDetailRowDeceased(detailRows[i], deceasedCodes)) {
      const code = (detailRows[i]?.f13 ?? '').toString().trim();
      if (code) return code;
    }
  }
  return stored;
}

/**
 * 決定通知書教示文非表示（世帯単位チェックボックス）の初期 ON/OFF を返す。
 * 仕様（業務確認 2026-06 改訂）: f56 は true=非表示 / false=表示。
 * 死亡者を除く明細の f56 が「すべて 1(非表示)」のときだけ ON（true）。
 * 1 件でも 1以外(0/空) があれば OFF（false）。非死亡明細が 0 件のときも OFF。
 *
 * @param detailRows 明細行（各行は f13/f17/f56 を含む {fieldKey -> value} マップ）
 * @param deathInfo  死亡情報入力（recipient.deathInfo。f13 のカンマ区切り）
 */
export function computeHideInstructionTextInitial(
  detailRows: Array<Record<string, unknown>>,
  deathInfo: string | null | undefined,
): boolean {
  const deceased = computeDeceasedMemberCodes(detailRows, deathInfo);
  const alive = detailRows.filter((row) => !isDetailRowDeceased(row, deceased));
  return alive.length > 0
    && alive.every((row) => normalizeHouseholdValue('f56', row.f56) === '1');
}

export function formatRecipientFieldValue(fieldKey: string, value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? '1' : '0';

  const text = String(value).trim();
  if (text === '') return '';
  if (!RECIPIENT_BOOLEAN_FIELD_KEYS.has(fieldKey)) return text;

  const normalized = text.toLowerCase();
  if (normalized === 'true') return '1';
  if (normalized === 'false') return '0';
  return text;
}

export const buildInitialRecipientData = () => {
  const preset: Record<string, string> = {
    f1: 'H-001234',
    f13: 'M-001',
    f52: '山田 太郎',
    f53: '1600023',
    f54: '東京都新宿区西新宿2-8-1',
    f64: '12345',
    f65: '01',
  };
  const householdFields = Object.fromEntries(HOUSEHOLD_FIELD_DEFS.map(({ key }) => [key, preset[key] ?? '—']));
  const memberFields = Object.fromEntries(MEMBER_FIELD_DEFS.map(({ key }) => [key, preset[key] ?? '—']));
  return {
    identId: 'SID-U-001234',
    householdIdentCode: 'H-001234',
    comment: '',
    householdFields,
    details: [{ id: 1, fields: memberFields }],
  };
};
